import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useHandler } from "@redotech/react-util/hook";
import {
  useCloseSupportTicketAction,
  useReopenSupportTicketAction,
  type SupportTicketAction,
} from "../actions/use-support-ticket-actions";

export interface UseSupportTicketActionToolbarResult {
  /** The primary close/reopen action based on current support ticket status */
  primaryAction: SupportTicketAction;
  /** The support ticket context for isEnabled/isVisible checks */
  supportTicket: SupportTicketListItemResponse;
  /** Execute the primary action */
  handlePrimaryAction: () => void;
}

/**
 * Hook that provides the primary support ticket action for a toolbar.
 * The full action menu is handled by SupportTicketActionsDropdown.
 */
export function useSupportTicketActionToolbar(
  supportTicketId: SupportTicketId,
  supportTicket: SupportTicketListItemResponse,
): UseSupportTicketActionToolbarResult {
  const closeAction = useCloseSupportTicketAction();
  const reopenAction = useReopenSupportTicketAction();

  const isClosed = supportTicket.status === "CLOSED";
  const primaryAction = isClosed ? reopenAction : closeAction;

  const handlePrimaryAction = useHandler(() => {
    const isEnabled = primaryAction.isEnabled?.(supportTicket) ?? true;
    if (isEnabled) {
      void primaryAction.execute(supportTicketId);
    }
  });

  return { primaryAction, supportTicket, handlePrimaryAction };
}
